import React, { useState, useEffect } from 'react'
import { Tag, Loader2 } from 'lucide-react'
import { Drawer, Input } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import usePartnerTierStore from '../../stores/partnerTierStore'

interface EditTierDrawerProps {
  isOpen: boolean
  onClose: () => void
}

const EditTierDrawer: React.FC<EditTierDrawerProps> = ({
  isOpen,
  onClose
}) => {
  const { showSuccess, showError } = useToast()
  const { selectedTier, updateTier } = usePartnerTierStore()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)

  // Initialize form when drawer opens or tier changes
  useEffect(() => {
    if (isOpen && selectedTier) {
      setName(selectedTier.name)
      setDescription(selectedTier.description || '')
      setIsActive(selectedTier.is_active)
    }
  }, [isOpen, selectedTier])

  const resetForm = () => {
    setName('')
    setDescription('')
    setIsActive(true)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedTier) return

    if (!name.trim()) {
      showError('Validation Error', 'Tier name is required')
      return
    }

    if (name.length > 100) {
      showError('Validation Error', 'Tier name must be 100 characters or less')
      return
    }

    // Check if anything changed
    const hasChanges =
      name.trim() !== selectedTier.name ||
      (description.trim() || null) !== selectedTier.description ||
      isActive !== selectedTier.is_active

    if (!hasChanges) {
      showError('No Changes', 'Please make changes before saving')
      return
    }

    setIsUpdating(true)

    try {
      const updated = await updateTier(selectedTier.id, {
        name: name.trim(),
        description: description.trim() || null,
        is_active: isActive
      })

      showSuccess(
        'Tier Updated',
        `"${updated.name}" has been updated successfully`
      )
      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showError(
        'Failed to update tier',
        errorMessage.includes('already exists')
          ? 'A tier with this name already exists'
          : 'Please try again later'
      )
    } finally {
      setIsUpdating(false)
    }
  }

  if (!selectedTier) return null

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Partner Tier"
      description={`Editing: ${selectedTier.name}`}
      icon={<Tag className="h-5 w-5" />}
      width="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Tier Name */}
        <div>
          <label
            htmlFor="edit-tier-name"
            className="block text-xs font-medium text-gray-700 mb-2"
          >
            Tier Name <span className="text-red-500">*</span>
          </label>
          <Input
            id="edit-tier-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Brand Partners"
            maxLength={100}
            disabled={isUpdating}
            className="w-full"
            autoFocus
          />
          <p className="text-xs text-gray-500 mt-1">
            {name.length}/100 characters
          </p>
        </div>

        {/* Description */}
        <div>
          <label
            htmlFor="edit-tier-description"
            className="block text-xs font-medium text-gray-700 mb-2"
          >
            Description
          </label>
          <textarea
            id="edit-tier-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter a description for this tier (optional)"
            rows={4}
            disabled={isUpdating}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
          />
        </div>

        {/* Active Status */}
        <div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={isUpdating}
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
            />
            <span className="text-sm text-gray-700">Active</span>
          </label>
          <p className="text-xs text-gray-500 mt-1">
            Only active tiers can be assigned to partners
          </p>
        </div>

        {/* Metadata */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Created:</span>
            <span className="text-gray-900 font-medium">
              {new Date(selectedTier.created_at).toLocaleString()}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Last Updated:</span>
            <span className="text-gray-900 font-medium">
              {new Date(selectedTier.updated_at).toLocaleString()}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Tier ID:</span>
            <span className="text-gray-900 font-mono text-xs">
              {selectedTier.id}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isUpdating}
            className="flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isUpdating || !name.trim()}
            className="flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Tag className="h-3.5 w-3.5 mr-1.5" />
                Update Tier
              </>
            )}
          </button>
        </div>
      </form>
    </Drawer>
  )
}

export default EditTierDrawer
